import { Box, Button, capitalize, Skeleton, Typography } from "@mui/material";
import { uniq } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import { ChartDisplayType } from "glue/Chart/chart.constants";
import { AssessmentVertical, InsightsAsset } from "graphql-types/graphql";
import {
    useAssetFilterTranslations,
    useBenchmarkTranslations,
    useRegionTranslation,
    useVerticalTranslations,
} from "hooks";
import { ChartTypeSwitch } from "legacyNext/components/Charts/ChartTypeSwitch";
import { Filter, FilterRecord } from "legacyNext/components/Filter/Filter";

import {
    getConsumptionSeries,
    useConsumptionChartData,
} from "./consumption.helpers";
import { ConsumptionSeries } from "./consumption.types";
import { ConsumptionChart } from "./ConsumptionChart";
import { ConsumptionTable } from "./ConsumptionTable";

const defaultFilter: FilterRecord = {
    user: ["tenant", "landlord"],
    vertical: [],
    benchmark: [],
};

export function Consumption() {
    const { asset } = useOutletContext<{ asset: InsightsAsset }>();
    const [type, setType] = useState<ChartDisplayType>(
        ChartDisplayType.EMISSIONS
    );
    const [filter, setFilter] = useState<FilterRecord>(defaultFilter);

    const { t } = useTranslation();
    const { translations } = useVerticalTranslations();
    const { translations: benchmarkTranslations } = useBenchmarkTranslations();
    const { getRegionName } = useRegionTranslation();
    const { translations: filterTranslations } = useAssetFilterTranslations();

    const { rawData, benchmarksSeries, chartData, loading } =
        useConsumptionChartData({
            asset,
            type,
            filterTranslations,
            benchmarkTranslations,
            getRegionName,
            t,
        });

    const series: ConsumptionSeries[] = getConsumptionSeries(
        t,
        Object.keys(chartData[0] || {}),
        type
    );

    const filteredBenchmarks = benchmarksSeries.filter((benchmark) =>
        filter.benchmark.some((b) => benchmark.id?.includes(b))
    );

    const filteredSeries = series.filter(
        ({ dataKey, vertical }) =>
            filter.vertical.some((filter) => filter === vertical) &&
            filter.user.some((user) =>
                dataKey?.toLowerCase().includes(user.toLowerCase())
            )
    );

    useEffect(() => {
        if (!loading) {
            setFilter((prev) => ({
                ...prev,
                vertical: uniq(series.map((serie) => serie.vertical)),
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    return (
        <>
            <Typography variant="h2">
                {t("legacyNext.consumptionTitle", "Total footprint")}
            </Typography>
            <Typography>
                {t(
                    "legacyNext.consumptionSubtitle",
                    "Denne oversigt giver et overblik over ejendommens forbrug vist som CO2 udledning og energi i kWh."
                )}
            </Typography>
            <Box display="flex" gap={5} mb={4} mt={4}>
                <Filter
                    filterKey="user"
                    setFilter={setFilter}
                    defaultValue={defaultFilter.user}
                    title={t(
                        "assetDetailsComponent.ownershipType",
                        "Ownership type"
                    )}
                    sections={[
                        {
                            options: [
                                { label: t("common.tenant"), value: "tenant" },
                                {
                                    label: t("common.landlord"),
                                    value: "landlord",
                                },
                                {
                                    label: t("common.mixed"),
                                    value: "mixed",
                                    disabled: true,
                                },
                            ],
                        },
                    ]}
                />
                <Filter
                    hideValues
                    filterKey="benchmark"
                    setFilter={setFilter}
                    defaultValue={defaultFilter.benchmark}
                    title={capitalize(t("benchmark.plural", "Benchmarks"))}
                    sections={[
                        {
                            options: benchmarksSeries.map((benchmark) => {
                                return {
                                    value: benchmark.id,
                                    label: benchmark.label,
                                };
                            }),
                        },
                    ]}
                />
                {loading ? (
                    <Skeleton variant="rounded" width="100%">
                        <Button />
                    </Skeleton>
                ) : (
                    <Filter
                        filterKey="vertical"
                        setFilter={setFilter}
                        defaultValue={uniq(
                            series.map((serie) => serie.vertical)
                        )}
                        title={t(
                            "common.labels.consumptionType",
                            "Consumption type"
                        )}
                        sections={[
                            {
                                options: [
                                    ...Object.values(AssessmentVertical)
                                        .map((vertical) => ({
                                            value: vertical,
                                            label: translations[vertical],
                                            disabled:
                                                series.some(
                                                    (serie) =>
                                                        serie.vertical ===
                                                        vertical
                                                ) === false,
                                        }))
                                        .sort(
                                            (a, b) =>
                                                Number(a.disabled) -
                                                Number(b.disabled)
                                        ),
                                ],
                            },
                        ]}
                    />
                )}
            </Box>
            <ChartTypeSwitch excludeArea current={type} onChange={setType} />
            <ConsumptionChart
                loading={loading}
                chartData={chartData}
                filteredSeries={filteredSeries}
                filteredBenchmarks={filteredBenchmarks}
            />
            {rawData && <ConsumptionTable data={rawData} type={type} />}
        </>
    );
}
